/**
 * Product version in the UI: the footer of the login windows and the "Version" dialog behind the help menu. The data
 * comes from GET /api/v1/version (which reveals more the more privileged the session is); the version embedded at
 * build time (@platform/config `resolveBuildInfo`) is the fallback while the API is unreachable.
 */
import { useI18n } from '@platform/i18n';
import { useEffect, useState } from 'react';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/table';
import { useBranding } from './branding';

export interface VersionInfo {
  /** `YYYY.MM.N`, with `+hash` when signed in. */
  version: string;
  commit?: string | undefined;
  builtAt?: string | null | undefined;
  /** Admin sessions only; a null version is unknown. */
  components?: readonly { name: string; version: string | null }[] | undefined;
}

declare const __PLATFORM_BUILD__: VersionInfo | undefined;

/** The version embedded in this bundle (undefined outside a Vite build that defines it). */
export function embeddedVersion(): VersionInfo | undefined {
  return typeof __PLATFORM_BUILD__ === 'undefined' ? undefined : __PLATFORM_BUILD__;
}

/** The part of a version before the build metadata (`2026.10.142+abc1234` → `2026.10.142`). */
export const publicVersion = (version: string): string => version.split('+')[0] ?? version;

/** Current version info; `undefined` while nothing is known (no API answer and nothing embedded). */
export function useVersionInfo(): VersionInfo | undefined {
  const { assetUrl } = useBranding();
  const [info, setInfo] = useState<VersionInfo | undefined>(embeddedVersion);
  useEffect(() => {
    if (typeof fetch !== 'function') return;
    let active = true;
    fetch(assetUrl('/api/v1/version'), { headers: { accept: 'application/json' }, cache: 'no-store' })
      .then((response) => (response.ok ? (response.json() as Promise<VersionInfo>) : undefined))
      .then((next) => {
        if (active && typeof next?.version === 'string') setInfo(next);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [assetUrl]);
  return info;
}

/** Small footer bar of the login windows: the version only (no commit or build time before sign-in). */
export function VersionFooter() {
  const { t } = useI18n();
  const info = useVersionInfo();
  if (!info) return null;
  return (
    <footer className="absolute inset-x-0 bottom-0 bg-background/80 px-4 py-1.5 text-center text-xs text-muted-foreground">
      {t('common.version.footer', { version: publicVersion(info.version) })}
    </footer>
  );
}

export interface VersionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Version, commit, build time and (for admins) the component versions; loaded when the dialog opens. */
function VersionDetails() {
  const { t, formatDate } = useI18n();
  const info = useVersionInfo();
  if (!info) return <p className="text-sm text-muted-foreground">{t('common.version.loading')}</p>;
  const unknown = t('common.version.unknown');
  const rows: [string, string][] = [
    [t('common.version.version'), info.version],
    [t('common.version.commit'), info.commit && info.commit !== 'unknown' ? info.commit.slice(0, 12) : unknown],
    [t('common.version.builtAt'), info.builtAt ? formatDate(info.builtAt, { dateStyle: 'medium', timeStyle: 'short' }) : unknown],
  ];
  return (
    <>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="contents">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="font-mono break-all">{value}</dd>
          </div>
        ))}
      </dl>
      {info.components && info.components.length > 0 && (
        <Table aria-label={t('common.version.components')}>
          <TableHeader>
            <TableRow>
              <TableHead>{t('common.version.components')}</TableHead>
              <TableHead>{t('common.version.version')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {info.components.map((component) => (
              <TableRow key={component.name}>
                <TableCell>{component.name}</TableCell>
                <TableCell className="font-mono">{component.version ?? unknown}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </>
  );
}

export function VersionDialog({ open, onOpenChange }: VersionDialogProps) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm" framed>
        <DialogHeader>
          <DialogTitle>{t('common.version.title')}</DialogTitle>
          <DialogDescription className="sr-only">{t('common.version.title')}</DialogDescription>
        </DialogHeader>
        <DialogBody className="flex flex-col gap-4">
          <VersionDetails />
          <a className="text-sm text-primary underline" href={`${import.meta.env.BASE_URL}gxrules-source.tar.gz`} download="gxrules-source.tar.gz">
            {t('common.version.bridgeSource')}
          </a>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
